// Modal/Dialog wrapper around Base UI's headless Dialog primitive.
//
// Replaces the legacy `.modal-backdrop` + manual onClick pattern with proper:
//   - focus trap (auto-focus first focusable, restore on close)
//   - escape-to-close
//   - body scroll lock
//   - aria-modal + aria-labelledby wired via Title slot
//
// Visual style stays identical to existing modals — same .card / .modal classes,
// same backdrop look. Behavior is what changes.
//
// Usage:
//   <Dialog open={open} onClose={() => setOpen(false)} maxWidth={460}>
//     <DialogHeader title="Apply" onClose={...}/>
//     <DialogBody>...</DialogBody>
//   </Dialog>

import React from 'react';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';

// Single-source viewport check for the `responsive` Dialog mode.
const useIsMobile = (breakpoint = 768, enabled = true) => {
  const [mobile, setMobile] = React.useState(
    () => enabled && typeof window !== 'undefined' && window.innerWidth < breakpoint,
  );
  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint, enabled]);
  return mobile;
};

// `responsive` prop: when true, on viewports <768px the dialog renders as a
// bottom-sheet drawer (slides up from bottom, full width, rounded top edge).
// On wider viewports it's the same centered modal. Used by the OAuth bind
// dialog so phones get a native-feeling drawer instead of a tiny modal.
export const Dialog = ({ open, onClose, maxWidth = 460, maxHeight = '90vh', responsive = false, children }) => {
  const isMobile = useIsMobile(768, responsive);
  const popupStyle = isMobile
    ? {
        // Bottom-sheet: pinned to bottom, full-width, rounded top.
        position: 'fixed',
        left: 0, right: 0, bottom: 0,
        top: 'auto',
        transform: 'none',
        width: '100%', maxWidth: '100%',
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column',
        zIndex: 91,
        background: 'var(--bg-1)',
        borderTop: '1px solid var(--line-2)',
        borderRadius: '14px 14px 0 0',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.7)',
      }
    : {
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100vw - 48px)', maxWidth,
        maxHeight,
        display: 'flex', flexDirection: 'column',
        zIndex: 91,
        background: 'var(--bg-1)',
        border: '1px solid var(--line-2)',
        borderRadius: 8,
        boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
      };
  return (
    <BaseDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          className="modal-backdrop"
          style={{ position: 'fixed', inset: 0, zIndex: 90 }}
        />
        <BaseDialog.Popup className="modal" style={popupStyle}>
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
};

// Standardised header with title + close button. `flex-shrink: 0` keeps it
// pinned at the top while DialogBody scrolls.
export const DialogHeader = ({ title, onClose, action }) => (
  <div
    className="row between"
    style={{
      padding: '18px 22px',
      borderBottom: '1px solid var(--line)',
      flexShrink: 0,
    }}
  >
    <BaseDialog.Title style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>{title}</BaseDialog.Title>
    {action || (onClose && (
      <button className="btn btn-sm btn-ghost btn-icon" onClick={onClose} aria-label="Close">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M3.5 3.5l9 9 M12.5 3.5l-9 9"/>
        </svg>
      </button>
    ))}
  </div>
);

// Scrollable body — grows to fill space, scrolls when content overflows.
// `min-height: 0` is critical: without it flex children refuse to shrink and
// `overflow-y: auto` does nothing.
export const DialogBody = ({ children, padding = 22, style }) => (
  <div
    style={{
      padding,
      overflowY: 'auto',
      flex: '1 1 auto',
      minHeight: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

// Sticky footer — sibling of header, pinned to bottom. Use this for action
// rows (Cancel / Continue) so they remain visible while body scrolls.
export const DialogFooter = ({ children, padding = '14px 22px', style }) => (
  <div
    className="row gap-2"
    style={{
      padding,
      borderTop: '1px solid var(--line)',
      flexShrink: 0,
      justifyContent: 'flex-end',
      background: 'var(--bg-1)',
      ...style,
    }}
  >
    {children}
  </div>
);

// Inline description for screen readers / supplementary text.
export const DialogDescription = ({ children, ...rest }) => (
  <BaseDialog.Description {...rest}>{children}</BaseDialog.Description>
);
